import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnInit,
} from '@angular/core';
import {
    BulkOperationResponse,
    ClientErrorCodes,
    IQueueItem,
    ModalResultType,
} from '@models';
import {
    GRID_SERVICE,
    ODataGridComponent,
    STATE_HANDLER,
    urlStateHandler,
} from '@pages/generics/grid';
import {
    FileDownloadService,
    ModalService,
    QueueDefinitionService,
    QueueItemService,
    SessionsService,
} from '@services';
import { IDropdownOption } from '@uipath/angular/components/ui-grid';

import cloneDeep from 'lodash-es/cloneDeep';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import {
    filter,
    map,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs/operators';

import {
    TransactionDetailsModalComponent,
    TransactionReviewerModalComponent,
    TransactionUpsertModalComponent,
} from '../../modals';
import {
    DEFAULT_EXPAND,
    DEFAULT_REVIEW_STATUS,
    excludedReviewStatus,
    PRIORITY_TYPE_OPTIONS,
    PROCESSING_EXCEPTION_TYPE_OPTIONS,
    queueItemToIdentifier,
    REVIEW_STATUS_OPTIONS,
    transactionStatusFor,
} from './transaction-items.constants';

@Component({
    selector: 'ui-transaction-items',
    templateUrl: './transaction-items.grid.html',
    providers: [
        QueueDefinitionService,
        SessionsService, {
            provide: GRID_SERVICE,
            useClass: QueueItemService,
        },
        {
            provide: STATE_HANDLER,
            useValue: urlStateHandler,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransactionsItemsGridComponent extends ODataGridComponent<IQueueItem, number> implements OnInit {
    @Input()
    public set queueId(id: number | undefined) {
        if (id != null) {
            this._filters$.next([
                ...this._filters$.value,
                {
                    value: Number(id),
                    method: 'eq',
                    property: 'queueDefinitionId',
                },
            ]);

            this._queueId = id;
        }
    }
    public get queueId() {
        return this._queueId;
    }

    @Input()
    public set userId(id: number | undefined) {
        if (id != null && this._userId !== id) {
            this._userId = id;
            this._filters$.next([
                ...this._filters$.value,
                {
                    method: 'eq',
                    value: id,
                    property: 'reviewerUser.id' as keyof IQueueItem,
                },
            ]);
        }
    }
    public get userId() {
        return this._userId;
    }

    @Input()
    public set isReviewMode(value: boolean) {
        this._isReviewMode = value;

        this._filters$.next([
            ...this._filters$.value,
            ...excludedReviewStatus(this.isReviewMode),
        ]);

        this._expand$.next(value ? [...DEFAULT_EXPAND, 'QueueDefinition'] : DEFAULT_EXPAND);

        this.transactionStatus = transactionStatusFor(this.isReviewMode);
    }
    public get isReviewMode() {
        return !!this._isReviewMode;
    }

    public get reviewFilter() {
        return this.isReviewMode ? REVIEW_STATUS_OPTIONS.find(s => s.label === DEFAULT_REVIEW_STATUS) : null;
    }

    public transactionStatus!: IDropdownOption[];

    public options = {
        review: REVIEW_STATUS_OPTIONS,
        priority: PRIORITY_TYPE_OPTIONS,
        processingException: PROCESSING_EXCEPTION_TYPE_OPTIONS,
    };

    public reportUrl$!: Observable<string>;
    public isQueuesViewGranted$ = this._appState.user.permissions.isGranted('Queues.View');
    public isRobotsViewGranted$ = this._appState.user.permissions.isGranted('Robots.View');
    public isTransactionsEditGranted$ = this._appState.user.permissions.isGranted('Transactions.Edit');

    public reviewerSuggest = (this._service as QueueItemService).suggestReviewers;
    public robotSuggest = this._injector.get<SessionsService>(SessionsService).suggest;
    public get queueSuggest() { return this._queueDefinition.suggest; }

    private _queueDefinition = this._injector.get<QueueDefinitionService>(QueueDefinitionService);

    private _userId?: number;
    private _isReviewMode?: boolean;
    private _queueId?: number;

    private _queueItemService = this._service as QueueItemService;
    private _modalService = this._injector.get<ModalService>(ModalService);
    private _downloadService = this._injector.get<FileDownloadService>(FileDownloadService);

    ngOnInit() {
        this.reportUrl$ = this._activeQuery$.pipe(
            map(qo => this._queueDefinition.getReportsUrl(this.queueId as number, qo)),
        );

    }

    public showDetails(entry: IQueueItem) {
        const clone: IQueueItem = cloneDeep(entry);
        this._modalService.openModal(TransactionDetailsModalComponent, clone, { size: 'lg' });
    }

    public edit(entry: IQueueItem, isDuplicate = false) {
        const clone: IQueueItem = cloneDeep(entry);
        this._queueDefinition.getQueueNameAndSlaById(entry.queueDefinitionId)
            .pipe(
                switchMap(({name, slaMinutes}) => {
                    const queueItemDto = {
                        id: clone.id,
                        name,
                        priority: clone.priority,
                        progress: clone.progress,
                        specificContent: clone.specificContent as any,
                        deferDate: clone.deferDate ? moment(clone.deferDate) : undefined,
                        dueDate: clone.dueDate ? moment(clone.dueDate) : undefined,
                        reference: clone.reference,
                        slaEnabled: slaMinutes > 0,
                    };

                    return this._modalService
                        .openModal(TransactionUpsertModalComponent, queueItemDto, { isDuplicate })
                        .afterClosedResult();
                }),
                takeUntil(this._destroyed$),
            ).subscribe(() => {
                this._refresh$.next();
            });
    }

    public clone(entry: IQueueItem) {
        this.edit(entry, true);
    }

    public setReviewer(item?: IQueueItem) {
        const queueItemIdentifiers = this._coerceItemSelection(item).map(queueItemToIdentifier);

        this._modalService
            .openModal(
                TransactionReviewerModalComponent,
                queueItemIdentifiers, {
                    size: 'sm',
                    overflow: true,
                })
            .afterClosedResult()
            .pipe(
                filter((res) => res.type === ModalResultType.Generic),
                map(res => res.data!),
                tap(() => this._refresh$.next()),
            )
            .subscribe(this._handleFailedItems);
    }

    public setState(state: 'InReview' | 'Verified' | 'Retried', item?: IQueueItem) {
        const queueItemIdentifiers = this._coerceItemSelection(item).map(queueItemToIdentifier);

        this.loading$.next(true);

        this._queueItemService
            .changeItemReviewStatus(queueItemIdentifiers, state)
            .pipe(
                tap(() => this._refresh$.next()),
            )
            .subscribe(this._handleFailedItems);
    }

    public isSelectable(item: IQueueItem) {
        return item.status !== 'InProgress';
    }

    public canPerformActionIf(status: string, item?: IQueueItem) {
        // All selected items must respect the following:
        // count(selected) is not 0
        // 1. status is in (Failed, Abandoned)
        // 2. review status is in (None, InReview)
        // If there is at least one item for which one of the rules does not apply
        // We cannot mark the group (return false)
        const statuses = ['Failed', 'Abandoned'];
        const reviewStatuses = ['None', status];

        const selectedItems = this._coerceItemSelection(item);

        return selectedItems
            .filter(qi => !(statuses.indexOf(qi.status) > -1
                && qi.reviewStatus && reviewStatuses.indexOf(qi.reviewStatus) > -1))
            .length === 0;
    }

    public canSetReviewer(item?: IQueueItem) {
        const statuses = ['Failed', 'Abandoned'];
        const reviewStatuses = ['None'];

        const selectedItems = this._coerceItemSelection(item);

        return selectedItems
            .filter(qi => !(statuses.indexOf(qi.status) > -1
                && qi.reviewStatus && reviewStatuses.indexOf(qi.reviewStatus) > -1))
            .length === 0;
    }

    public canDelete(item?: IQueueItem) {
        // All selected items must respect the following:
        // count(selected) is not 0
        // 1. status is not in (InProgress, Deleted)
        // If there is at least one item for which this rule does not apply
        // We cannot mark the group (return false)
        const notPermittedStatuses = ['InProgress', 'Deleted'];

        const selectedItems = this._coerceItemSelection(item);

        return selectedItems
            .filter(qi => notPermittedStatuses.indexOf(qi.status) > -1).length === 0;
    }

    public removeQueueItems(item?: IQueueItem) {
        const queueItemIdentifiers = this._coerceItemSelection(item).map(queueItemToIdentifier);

        this._modalService.onConfirm(null,
            () => {
                this.loading$.next(true);

                this._queueItemService
                    .deleteBulk(queueItemIdentifiers)
                    .pipe(
                        tap(() => this._refresh$.next()),
                    )
                    .subscribe(this._handleFailedItems);
            },
            () => { },
            this._translate.instant('grid_action_delete_message'));
    }

    public canEdit(item: IQueueItem) {
        return ['New', 'Failed', 'Abandoned'].includes(item.status);
    }

    public canClone(item: IQueueItem) {
        return ['New', 'Failed', 'Successful', 'Abandoned'].includes(item.status);
    }

    public downloadReport(event: MouseEvent, reportUrl: string) {
        event.preventDefault();
        this._downloadService.download(reportUrl);
    }

    protected _ouDependentFilters() {
        return ['queueDefinition.id', 'robot.id'];
    }

    private _handleFailedItems = (response: BulkOperationResponse) => {
        if (response.success) {
            this._grid.selectionManager.clear();
            return;
        }

        this._snack.error(`${response.message} (#${ClientErrorCodes.TransactionItemsFailed})`);
        this._grid.selectionManager.selected.forEach(queueItem => {
            const isFailed = response.failedItems.includes(queueItem.id);

            if (!isFailed) {
                this._grid.selectionManager.deselect(queueItem);
            }
        });
    }

    private _coerceItemSelection = (item?: IQueueItem) => item != null ?
        [(item)] :
        this._grid.selectionManager.selected
}
